import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { lanceDaPartida, rotuloDeFase, useT } from "../../i18n";
import { Crest } from "../../ui/atoms";
import { tocarGol } from "../../ui/somDeGol";
import { BotaoDoClube } from "../FichaDoClube";
import { CampoAoVivo } from "./CampoAoVivo";
import { PlacaresDaRodada } from "./PlacaresDaRodada";
import { SubstituicaoNoCampo, type TrocaDaPartida } from "./SubstituicaoNoCampo";
import type { LanceDaPartida, PartidaView } from "./tipos";
import "./partida.css";

/**
 * A partida em página inteira.
 *
 * À esquerda os placares da rodada, à direita o campo com os vinte e dois, a
 * bola e os gols, e embaixo os lances e os números. A substituição toma o
 * lugar do campo com a cara da tela de tática. No celular tudo empilha, o
 * campo fica em pé e os controles descem para o rodapé.
 *
 * A página não tem relógio próprio: quem manda o tempo é quem a usa. No solo,
 * o passo do motor; no online, a chegada das mensagens. O que ela sincroniza é
 * o que aparece: a lista, o placar e o aviso de gol só mudam quando o campo
 * chega àquele lance, e não quando o lance chega do motor.
 */

export type Situacao = "jogando" | "pausado" | "intervalo" | "fim";

export function Partida({
  partida,
  velocidade,
  orcamentoMs,
  parado,
  situacao,
  controles,
  troca,
  trocaAberta,
  aviso,
  acaoDoFim,
  aoDesenhar,
  aoAbrirClube,
}: {
  partida: PartidaView;
  /** A velocidade da física: 1 natural, 2 e 4 aceleradas, 0 desenha na hora. */
  velocidade: number;
  /** Tempo real por leva, quando os lances chegam sem esperar o desenho (online). */
  orcamentoMs?: number;
  /** Nada anda: pausa, intervalo, substituição. */
  parado: boolean;
  situacao: Situacao;
  /** Os botões do alto (e do rodapé, no celular). */
  controles: ReactNode;
  /** A substituição, quando quem assiste tem clube na partida. */
  troca: TrocaDaPartida | null;
  trocaAberta: boolean;
  /** Um recado no alto: conexão caída, por exemplo. */
  aviso?: ReactNode;
  /** O botão do fim de jogo. */
  acaoDoFim?: ReactNode;
  /** Avisa quem usa a página quando o campo começa e termina de desenhar uma leva. */
  aoDesenhar?: (desenhando: boolean) => void;
  /** Tocar em outro clube (no placar ou na rodada) abre a ficha dele. */
  aoAbrirClube?: ((clubId: string) => void) | undefined;
}): ReactNode {
  const t = useT();
  const [visiveis, definirVisiveis] = useState(() => partida.lances.length);
  const [desenhando, definirDesenhando] = useState(false);
  const aoDesenharRef = useRef(aoDesenhar);
  aoDesenharRef.current = aoDesenhar;
  const [golAviso, definirGolAviso] = useState<{ nome: string; minuto: number; contra: boolean; clube: string } | null>(null);
  const [aba, definirAba] = useState<"lances" | "numeros">("lances");
  const listaRef = useRef<HTMLDivElement | null>(null);

  /*
   * O relógio corre de verdade: cada leva de lances cobre um trecho de cinco
   * minutos, e o minuto anda do começo ao fim do trecho no tempo que o campo
   * prevê para desenhá-la, em vez de pular de lance em lance.
   */
  const relogio = useRef({ inicio: 0, duracao: 1, de: 0, ate: 0, mostrado: 0 });
  const [minutoDoRelogio, definirMinutoDoRelogio] = useState(() => minutoDaLeva(partida.lances, partida.minute));
  useEffect(() => {
    const ate = minutoDaLeva(partida.lances, partida.minute);
    if (ate !== relogio.current.ate) relogio.current.ate = ate;
    if (velocidade <= 0) {
      relogio.current = { ...relogio.current, de: ate, ate, mostrado: ate, inicio: performance.now(), duracao: 1 };
      definirMinutoDoRelogio(ate);
    }
  }, [partida.lances.length, partida.minute, velocidade]);
  const aoDesenharCampo = useCallback((agora: boolean, previstoMs?: number) => {
    definirDesenhando(agora);
    const r = relogio.current;
    if (agora && previstoMs !== undefined) {
      relogio.current = { ...r, inicio: performance.now(), duracao: Math.max(200, previstoMs), de: r.mostrado };
    } else if (!agora) {
      relogio.current = { ...r, de: r.ate, mostrado: r.ate, inicio: performance.now(), duracao: 1 };
    }
    aoDesenharRef.current?.(agora);
  }, []);
  useEffect(() => {
    let vivo = true;
    let id = 0;
    const tique = (): void => {
      if (!vivo) return;
      const r = relogio.current;
      const fracao = Math.min(1, (performance.now() - r.inicio) / r.duracao);
      const minuto = Math.floor(r.de + (r.ate - r.de) * fracao);
      r.mostrado = Math.max(r.mostrado, Math.min(r.ate, minuto));
      const mostrado = r.mostrado;
      definirMinutoDoRelogio((atual) => (atual === mostrado ? atual : mostrado));
      id = requestAnimationFrame(tique);
    };
    id = requestAnimationFrame(tique);
    return () => {
      vivo = false;
      cancelAnimationFrame(id);
    };
  }, []);

  // Sem tempo para desenhar, tudo aparece de uma vez.
  useEffect(() => {
    if (velocidade <= 0) definirVisiveis(partida.lances.length);
  }, [velocidade, partida.lances.length]);
  useEffect(() => {
    if (partida.lances.length < visiveis) definirVisiveis(partida.lances.length);
  }, [partida.lances.length, visiveis]);

  const aoMostrar = useCallback(
    (lance: LanceDaPartida) => {
      definirVisiveis((atual) => Math.max(atual, lance.ordem + 1));
      if (lance.kind === "gol" && lance.playerId) {
        const contra = partida.myClubId !== null && lance.clubId !== partida.myClubId;
        const clube = lance.clubId === partida.home.clubId ? partida.home : partida.away;
        definirGolAviso({ nome: String(lance.params["jogador"] ?? ""), minuto: lance.minute, contra, clube: clube.short });
        tocarGol(!contra);
      }
    },
    [partida.myClubId, partida.home, partida.away],
  );
  useEffect(() => {
    if (!golAviso) return;
    const id = window.setTimeout(() => definirGolAviso(null), 1900);
    return () => window.clearTimeout(id);
  }, [golAviso]);

  const lancesVisiveis = useMemo(() => partida.lances.slice(0, visiveis), [partida.lances, visiveis]);
  const acabou = partida.finished && !desenhando;
  const golsDe = (clubId: string): number =>
    acabou
      ? clubId === partida.home.clubId
        ? partida.homeGoals
        : partida.awayGoals
      : lancesVisiveis.filter((l) => l.kind === "gol" && l.playerId && l.clubId === clubId).length;
  const golsCasa = golsDe(partida.home.clubId);
  const golsFora = golsDe(partida.away.clubId);
  const minuto = acabou ? 90 : Math.min(90, minutoDoRelogio);
  const noSegundoTempo = minuto > 45 || (minuto === 45 && situacao !== "intervalo" && partida.minute > 45);

  const goleadores = (clubId: string): string =>
    lancesVisiveis
      .filter((l) => l.kind === "gol" && l.playerId && l.clubId === clubId)
      .map((l) => `${nomeDoLance(l)} ${String(l.minute)}'`)
      .join(", ");

  // A lista de lances rola sozinha.
  useEffect(() => {
    const caixa = listaRef.current;
    if (caixa) caixa.scrollTop = caixa.scrollHeight;
  }, [visiveis]);

  const meuGol = partida.myClubId === partida.home.clubId ? golsCasa : partida.myClubId === partida.away.clubId ? golsFora : null;
  const golDele = partida.myClubId === partida.home.clubId ? golsFora : partida.myClubId === partida.away.clubId ? golsCasa : null;
  const corPlacar =
    meuGol === null || golDele === null
      ? "var(--text)"
      : meuGol > golDele
        ? "var(--success)"
        : meuGol < golDele
          ? "var(--danger)"
          : "var(--text)";

  const periodo = noSegundoTempo ? t("partida.segundoTempo") : t("partida.primeiroTempo");
  const tempo =
    situacao === "fim" && acabou
      ? t("aoVivo.fimDeJogo")
      : situacao === "intervalo"
        ? `${t("aoVivo.intervalo")} · ${t("aoVivo.minuto", { minuto: 45 })}`
        : situacao === "pausado"
          ? `${t("aoVivo.pausado")} · ${t("aoVivo.minuto", { minuto })}`
          : `${periodo} · ${t("aoVivo.minuto", { minuto })}`;
  const fase = rotuloDeFase(partida.stage);

  // O próprio clube não abre: a ficha é para olhar o outro lado.
  const abrirOutro = (clubId: string): ((clubId: string) => void) | undefined =>
    aoAbrirClube && clubId !== partida.myClubId ? aoAbrirClube : undefined;

  const numeros = useMemo(() => contar(lancesVisiveis, partida.home.clubId, partida.away.clubId), [lancesVisiveis, partida.home.clubId, partida.away.clubId]);

  return (
    <div className="pf" aria-label={t("aoVivo.aria")}>
      <header className="pf-topo card">
        <div className="pf-topo-info">
          <span className="label-muted corta">{fase ? `${partida.competition} · ${fase}` : partida.competition}</span>
          {aviso && <span className="pf-aviso">{aviso}</span>}
        </div>
        <div className="pf-placar-bloco">
          <div className="pf-placar">
            <BotaoDoClube
              clubId={partida.home.clubId}
              nome={partida.home.name}
              aoAbrir={abrirOutro(partida.home.clubId)}
              className="pf-placar-lado"
            >
              <span className="corta">{partida.home.name}</span>
              <Crest colors={partida.home.colors} short={partida.home.short} size={34} />
            </BotaoDoClube>
            <span key={`${String(golsCasa)}-${String(golsFora)}`} className="num pf-placar-gols" style={{ color: corPlacar }}>
              {golsCasa} x {golsFora}
            </span>
            <BotaoDoClube
              clubId={partida.away.clubId}
              nome={partida.away.name}
              aoAbrir={abrirOutro(partida.away.clubId)}
              className="pf-placar-lado dir"
            >
              <span className="corta">{partida.away.name}</span>
              <Crest colors={partida.away.colors} short={partida.away.short} size={34} />
            </BotaoDoClube>
          </div>
          <div className="pf-placar-goleadores">
            <span>{goleadores(partida.home.clubId)}</span>
            <span />
            <span>{goleadores(partida.away.clubId)}</span>
          </div>
          <div className="num pf-placar-tempo" data-parado={situacao !== "jogando" ? "" : undefined}>
            {tempo}
          </div>
        </div>
        <div className="pf-controles">{controles}</div>
        <div className="pf-progresso">
          <i style={{ width: `${String(Math.min(100, (minuto / 90) * 100))}%` }} />
        </div>
      </header>

      <div className={`pf-corpo${partida.placares.length === 0 ? " sem-rodada" : ""}`}>
        {partida.placares.length > 0 && (
          <PlacaresDaRodada
            placares={partida.placares}
            minuto={minuto}
            encerrada={acabou}
            aoAbrirClube={aoAbrirClube}
            meuClube={partida.myClubId}
          />
        )}

        {trocaAberta && troca && <SubstituicaoNoCampo troca={troca} minuto={minuto} />}

        {/*
          O campo continua montado durante a substituição, só escondido.
          Desmontá-lo mataria a fila de cenas e o relógio do desenho, e ao
          voltar ele recomeçaria a partida do zero.
        */}
        <section className={`pf-jogo${trocaAberta && troca ? " pf-escondido" : ""}`}>
            <div className="pf-palco card">
              <CampoAoVivo
                partida={partida}
                velocidade={velocidade}
                orcamentoMs={orcamentoMs}
                parado={parado}
                intervalo={situacao === "intervalo"}
                aoMostrar={aoMostrar}
                aoDesenhar={aoDesenharCampo}
              />
              <div className={`pf-gol-aviso${golAviso ? " mostra" : ""}`} data-contra={golAviso?.contra ? "" : undefined} aria-live="polite">
                {golAviso && (
                  <>
                    <div className="pf-gol-titulo">{golAviso.contra ? t("partida.golContra", { clube: golAviso.clube }) : t("partida.gol")}</div>
                    <div className="pf-gol-quem">
                      {golAviso.nome} · {t("aoVivo.minuto", { minuto: golAviso.minuto })}
                    </div>
                  </>
                )}
              </div>
              {acabou && (
                <div className="pf-fim-aviso">
                  <span className="label-muted">{t("aoVivo.fimDeJogo")}</span>
                  <b className="num">
                    {golsCasa} x {golsFora}
                  </b>
                  {acaoDoFim}
                </div>
              )}
            </div>

            <div className="pf-abas" role="group">
              <button type="button" className="ghost" aria-pressed={aba === "lances"} onClick={() => definirAba("lances")}>
                {t("aoVivo.abaLances")}
              </button>
              <button type="button" className="ghost" aria-pressed={aba === "numeros"} onClick={() => definirAba("numeros")}>
                {t("partida.numeros")}
              </button>
            </div>

            <div className="pf-rodape">
              <div className={`pf-lances card${aba !== "lances" ? " pf-fora-da-aba" : ""}`}>
                <div className="pf-bloco-topo">
                  <span className="label-muted">{t("aoVivo.abaLances")}</span>
                  <span className="num muted">{lancesVisiveis.filter((l) => l.kind !== "posse" && l.kind !== "outro").length}</span>
                </div>
                <div className="scroll" ref={listaRef}>
                  <div className="pf-lances-lista">
                    {lancesVisiveis.map((lance) => {
                      if (lance.kind === "posse" || lance.kind === "outro") return null;
                      const contra = partida.myClubId !== null && lance.clubId !== partida.myClubId;
                      return (
                        <div
                          key={lance.ordem}
                          className={`pf-lance${lance.kind === "gol" ? " gol" : ""}${lance.kind === "gol" && contra ? " contra" : ""}`}
                        >
                          <span className="num min">{t("aoVivo.minuto", { minuto: lance.minute })}</span>
                          <span className={`pf-icone ${lance.kind}${lance.kind === "gol" && contra ? " contra" : ""}`}>{LETRA[lance.kind] ?? ""}</span>
                          <span>{lanceDaPartida(lance.key, lance.params)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className={`pf-numeros card${aba !== "numeros" ? " pf-fora-da-aba" : ""}`}>
                <div className="pf-bloco-topo">
                  <span className="label-muted">{t("partida.numeros")}</span>
                  <span className="muted" style={{ fontSize: 11 }}>
                    {partida.home.short} · {partida.away.short}
                  </span>
                </div>
                <div className="pf-numeros-lista">
                  <Numero rotulo={t("partidaOnline.posse")} a={numeros.posse.casa} b={numeros.posse.fora} sufixo="%" />
                  <Numero rotulo={t("partidaOnline.finalizacoes")} a={numeros.fin.casa} b={numeros.fin.fora} />
                  <Numero rotulo={t("partida.noGol")} a={numeros.noGol.casa} b={numeros.noGol.fora} />
                  <Numero rotulo={t("partidaOnline.cartoes")} a={numeros.cartoes.casa} b={numeros.cartoes.fora} />
                </div>
              </div>
            </div>
          </section>
      </div>
    </div>
  );
}

const LETRA: Partial<Record<LanceDaPartida["kind"], string>> = {
  gol: "G",
  chance: "C",
  defesa: "D",
  amarelo: "",
  vermelho: "",
  lesao: "+",
  substituicao: "S",
  penalti_perdido: "P",
  falta: "F",
  impedimento: "I",
};

/** O minuto em que a última leva termina: o fim do trecho de cinco minutos dela. */
function minutoDaLeva(lances: LanceDaPartida[], minutoDaVista: number): number {
  const maior = lances.reduce((m, l) => (l.minute < 91 ? Math.max(m, l.minute) : m), 0);
  if (maior === 0) return Math.min(90, minutoDaVista);
  return Math.min(90, Math.ceil(maior / 5) * 5);
}

function nomeDoLance(lance: LanceDaPartida): string {
  const nome = String(lance.params["jogador"] ?? "");
  const partes = nome.trim().split(/\s+/);
  return partes.length > 1 ? (partes[partes.length - 1] ?? nome) : nome;
}

function contar(lances: LanceDaPartida[], casa: string, fora: string) {
  const zero = () => ({ casa: 0, fora: 0 });
  const posse = zero();
  const fin = zero();
  const noGol = zero();
  const cartoes = zero();
  const lado = (clubId: string | null): "casa" | "fora" | null => (clubId === casa ? "casa" : clubId === fora ? "fora" : null);
  for (const lance of lances) {
    const l = lado(lance.clubId);
    if (!l) continue;
    const outro = l === "casa" ? "fora" : "casa";
    switch (lance.kind) {
      case "posse":
        posse[l] += 1;
        break;
      case "gol":
        if (lance.playerId) {
          fin[l] += 1;
          noGol[l] += 1;
        }
        break;
      case "chance":
      case "penalti_perdido":
        fin[l] += 1;
        break;
      case "defesa":
        fin[outro] += 1;
        noGol[outro] += 1;
        break;
      case "amarelo":
      case "vermelho":
        cartoes[l] += 1;
        break;
      default:
        break;
    }
  }
  const total = posse.casa + posse.fora;
  const posseCasa = total > 0 ? Math.round((posse.casa / total) * 100) : 50;
  return { posse: { casa: posseCasa, fora: 100 - posseCasa }, fin, noGol, cartoes };
}

function Numero({ rotulo, a, b, sufixo = "" }: { rotulo: string; a: number; b: number; sufixo?: string }): ReactNode {
  const soma = Math.max(1, a + b);
  return (
    <div className="pf-numero">
      <span className="num">
        {a}
        {sufixo}
      </span>
      <div className="pf-numero-meio">
        <span className="label-muted">{rotulo}</span>
        <div className="pf-barra">
          <i style={{ width: `${String((a / soma) * 100)}%` }} />
          <i style={{ width: `${String((b / soma) * 100)}%` }} />
        </div>
      </div>
      <span className="num dir">
        {b}
        {sufixo}
      </span>
    </div>
  );
}
